import {getTestProps} from '@benchling/js-test-utils';
import {Radio, Stack} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {assert} from 'Asserts';
import * as BenchlingUtil from 'BenchlingUtil';
import {
  ActionOrigin,
  COMMON_ANALYTICS_ACTIONS,
  WORKFLOWS_ANALYTICS_ACTIONS,
} from 'constants/AnalyticsConstants';
import {EntitySummary} from 'constants/UserConstants';
import {ProcessClientOrigin, ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import notifications from 'notifications';
import {processTaskSchemaURL} from 'processes/dao/ProcessSchemaDAO';
import * as ProcessTaskDefinitionDAO from 'processes/dao/ProcessTaskDefinitionDAO';
import {getSchemaOwnerChoices} from 'schemas/spec/SchemaSpecUtil';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {Header3} from 'ui-library/Headers';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {FieldRequiredIndicator} from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';
import {withLoadingScreen} from 'util/Promises';

import {ProcessStatusLifecyclesQuery} from './__generated/ProcessStatusLifecyclesQuery';
import {isConfigurableTaskDefinition} from './graph-builder-flowchart-config/horizontal-flowchart/useAllSchemasById';
import {NameField, OwnerField, PrefixField} from './ProcessSchemaMetadataSectionRow';
import {STATUS_LIFECYCLES_QUERY} from './ProcessTaskSchemaEditor';
import {DEFAULT_TASK_SCHEMA_PREFIX, DEFAULT_OUTPUT_SCHEMA_PREFIX} from './ProcessTaskSchemaEditorConstants';
import {cacheUpdatedTaskSchema} from './ProcessTaskSchemaUtils';

enum FlowchartOutputsChoice {
  YES = 'outputs',
  NO = 'no-outputs',
}

export interface CreateFlowchartTaskSchemaModalProps {
  onRequestClose: () => void;
}

export default function CreateFlowchartTaskSchemaModal({
  onRequestClose,
}: CreateFlowchartTaskSchemaModalProps): JSX.Element {
  const statusLifecyclesResult = useBenchlingQuery<ProcessStatusLifecyclesQuery>(STATUS_LIFECYCLES_QUERY);

  const [name, setName] = useState<string | null>(null);
  const [taskGroupPrefix, setTaskGroupPrefix] = useState<string | null>(null);
  const [owner, setOwner] = useState<EntitySummary | null>(null);
  const [flowchartOutputsChoice, setFlowchartOutputsChoice] = useState<FlowchartOutputsChoice | undefined>(
    undefined
  );
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const ownerChoices = getSchemaOwnerChoices();

  useEffect(() => {
    analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.OPENED_MODAL, ANALYTICS_MODULE.PROCEDURES, {
      modalType: 'create_flowchart_task_schema',
    });
  }, []);

  const getIsDisabled = (): boolean => {
    if (statusLifecyclesResult.type !== QueryResultType.SUCCESS) {
      return true;
    } else if (name == null || name === '') {
      return true;
    } else if (taskGroupPrefix == null || taskGroupPrefix === '') {
      return true;
    } else if (owner == null) {
      return true;
    } else if (flowchartOutputsChoice == null) {
      return true;
    }
    return false;
  };

  const isDisabled = getIsDisabled();

  return (
    <Modal
      title="Create a new flowchart"
      onRequestSubmit={async () => {
        if (isDisabled) {
          return;
        }
        assert(
          name != null && taskGroupPrefix != null && owner != null && flowchartOutputsChoice != null,
          'Expected all required fields to be filled'
        );
        assert(
          statusLifecyclesResult.type === QueryResultType.SUCCESS,
          'Expected status lifecycles to be loaded'
        );
        const statusLifecycle = statusLifecyclesResult.data.processTaskStatusLifecycles.find(
          (lifecycle) => lifecycle.schemaExecutionType === ProcessExecutionType.FLOWCHART
        );
        assert(statusLifecycle != null, 'Expected status lifecycle to be found');
        setErrors({});
        try {
          const taskDefinition = await withLoadingScreen(
            ProcessTaskDefinitionDAO.createProcessTaskDefinition({
              organizationId: owner.id,
              name,
              firstVersion: {
                taskSchemaSpec: {
                  name,
                  taskGroupPrefix,
                  organizationId: owner.id,
                  prefix: DEFAULT_TASK_SCHEMA_PREFIX,
                  schemaFields: [],
                  executionType: ProcessExecutionType.FLOWCHART,
                  clientOrigin: ProcessClientOrigin.PROCESSES,
                  canSetAssigneeOnTaskCreation: false,
                  defaultResponsiblePartiesIds: [],
                  statusLifecycleId: statusLifecycle.id,
                  processOutputSchema:
                    flowchartOutputsChoice === FlowchartOutputsChoice.YES
                      ? {
                          prefix: DEFAULT_OUTPUT_SCHEMA_PREFIX,
                          schemaFields: [],
                        }
                      : null,
                },
                applicationData: null,
              },
            }),
            'Creating flowchart...'
          );
          assert(isConfigurableTaskDefinition(taskDefinition), 'Expected task definition to be configurable');
          const newTaskSchema = taskDefinition.latestVersion.processTaskSchema;
          cacheUpdatedTaskSchema(newTaskSchema);
          analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.CREATED_FLOWCHART, {
            actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
            taskSchemaId: newTaskSchema.id,
            hasOutputs: flowchartOutputsChoice === FlowchartOutputsChoice.YES,
          });
          BenchlingUtil.navigate(null, processTaskSchemaURL(newTaskSchema.id));
          onRequestClose();
          // eslint-disable-next-line benchlint/always-throw-from-catch
        } catch (e) {
          if (e.responseJSON) {
            setErrors(e.responseJSON.errors ?? {});
            notifications.error('The flowchart could not be created');
          } else {
            notifications.notifyError(e);
          }
        }
      }}
      onRequestClose={onRequestClose}
      buttons={[
        {
          text: 'Create',
          submit: true,
          style: ButtonStyle.PRIMARY,
          disabled: isDisabled,
        },
      ]}
    >
      {statusLifecyclesResult.type === QueryResultType.SUCCESS ? (
        <>
          <div className={css(styles.fieldRowWrapper)}>
            <div className={css(styles.basicInfoField, styles.fieldWithMarginRight)}>
              <NameField
                label="Name"
                value={name ?? ''}
                isBeingCreated={true}
                onChange={setName}
                error={errors.processTaskSchemaName}
                isRequired={true}
                dataTestAttrs={{component: 'CreateFlowchartTaskSchemaModal', element: 'nameField'}}
              />
            </div>
            <div className={css(styles.basicInfoField)}>
              <PrefixField
                label="Task group prefix"
                value={taskGroupPrefix ?? ''}
                isBeingCreated={true}
                isRequired={true}
                onChange={setTaskGroupPrefix}
                error={errors.taskGroupPrefix}
                dataTestAttrs={{component: 'CreateFlowchartTaskSchemaModal', element: 'taskGroupPrefixField'}}
              />
            </div>
          </div>
          <div className={css(styles.ownerFieldWrapper)}>
            <OwnerField
              ownerChoices={ownerChoices}
              owner={owner}
              onOwnerChanged={setOwner}
              isEditable={true}
              isRequired={true}
              className={css(styles.basicInfoField, styles.fieldWithMarginRight)}
              dataTestAttrs={{component: 'CreateFlowchartTaskSchemaModal', element: 'ownerField'}}
            />
          </div>
          <div>
            <Header3>
              Include outputs in the flowchart?
              <FieldRequiredIndicator />
            </Header3>
            <InlineBanner
              bannerType={InlineBannerType.INFO}
              text="You won't be able to change this after creating the flowchart."
            />
            <Radio.Group
              value={flowchartOutputsChoice}
              onChange={(value) => {
                setFlowchartOutputsChoice(value as FlowchartOutputsChoice);
              }}
              withAsterisk={true}
              style={{marginTop: '16px'}}
            >
              <Stack>
                <Radio
                  value={FlowchartOutputsChoice.YES}
                  label="Yes, I want to define the outputs this flowchart produces"
                  {...getTestProps({
                    component: 'CreateFlowchartTaskSchemaModal',
                    element: 'flowchartOutputsChoice',
                    key: 'yesRadioButton',
                  })}
                />
                <Radio
                  value={FlowchartOutputsChoice.NO}
                  label="No, I don't want to define the outputs this flowchart produces"
                  {...getTestProps({
                    component: 'CreateFlowchartTaskSchemaModal',
                    element: 'flowchartOutputsChoice',
                    key: 'noRadioButton',
                  })}
                />
              </Stack>
            </Radio.Group>
          </div>
        </>
      ) : (
        <CenteredHorizontalThrobber />
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  ownerFieldWrapper: {
    width: '100%',
    marginBottom: '24px',
  },
  fieldRowWrapper: {
    width: '100%',
    marginBottom: '24px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  basicInfoField: {
    flex: 0.5,
  },
  fieldWithMarginRight: {
    marginRight: 16,
  },
});
